import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, CalendarDays, CalendarRange, Settings2, Check,
  X, Loader2, AlertTriangle, Gauge,
} from "lucide-react";
import { API, axios } from "@/api";
import { toast } from "sonner";

const TEMPO_OPTIONS = [
  { id: "one_big_day", label: "One Big Day", desc: "Everything on your birthday", icon: Calendar },
  { id: "birthday_week", label: "Birthday Week", desc: "Spread across 7 days", icon: CalendarDays },
  { id: "birthday_month", label: "Birthday Month", desc: "Your whole birthday month", icon: CalendarRange },
  { id: "custom", label: "Custom", desc: "Pick your available days", icon: Settings2 },
];

export default function ChangePaceSheet({ isOpen, onClose, currentTempo, themeColor, birthday, onTempoChanged }) {
  const [selected, setSelected] = useState(currentTempo || "one_big_day");
  const [customDates, setCustomDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const sheetRef = useRef(null);
  const color = themeColor || "#0ea5e9";

  useEffect(() => {
    setSelected(currentTempo || "one_big_day");
    setWarnings([]);
  }, [currentTempo, isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("mousedown", handleClick); document.removeEventListener("keydown", handleKey); };
  }, [isOpen, onClose]);

  const handleConfirm = async () => {
    if (selected === currentTempo && selected !== "custom") {
      onClose();
      return;
    }
    if (selected === "custom" && customDates.length === 0) {
      toast.error("Pick at least one day");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/itinerary/change-tempo`, {
        collection_tempo: selected,
        custom_dates: selected === "custom" ? customDates : [],
      });
      const data = res.data;
      if (data.expiration_warnings?.length > 0) {
        setWarnings(data.expiration_warnings);
      }
      toast.success(data.message);
      onTempoChanged(data);
      if (!data.expiration_warnings?.length) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change pace");
    } finally {
      setLoading(false);
    }
  };

  const toggleDate = (dateStr) => {
    setCustomDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort());
  };

  const getDateRange = () => {
    const dates = [];
    const start = new Date();
    for (let i = 0; i < 45; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const isChanged = selected !== currentTempo || selected === "custom";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50"
          />
          {/* Sheet */}
          <motion.div
            ref={sheetRef}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto shadow-2xl"
            data-testid="change-pace-sheet"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-200" />
            </div>

            <div className="px-4 pb-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-extrabold text-slate-800">Change Pace</h3>
                  <p className="text-[11px] text-slate-400">Redistribute stops — nothing deleted</p>
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="close-pace-sheet">
                  <X className="w-4 h-4 text-slate-500" />
                </button>
              </div>

              {/* 2x2 Grid */}
              <div className="grid grid-cols-2 gap-2.5" data-testid="pace-tempo-grid">
                {TEMPO_OPTIONS.map((tempo) => {
                  const Icon = tempo.icon;
                  const isCurrent = tempo.id === currentTempo;
                  const isSelected = tempo.id === selected;
                  return (
                    <button
                      key={tempo.id}
                      onClick={() => { setSelected(tempo.id); setWarnings([]); }}
                      className={`relative p-3.5 rounded-xl text-left transition-all ${
                        isSelected ? "ring-2 shadow-md" : "border border-slate-200 hover:border-slate-300"
                      }`}
                      style={isSelected ? { '--tw-ring-color': color, borderColor: color, backgroundColor: `${color}08` } : {}}
                      data-testid={`pace-${tempo.id}`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: color }}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      {isCurrent && !isSelected && (
                        <div className="absolute top-2 right-2 text-[8px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">Current</div>
                      )}
                      <Icon className="w-5 h-5 mb-2" style={isSelected ? { color } : { color: "#94a3b8" }} />
                      <p className="text-xs font-bold text-slate-800">{tempo.label}</p>
                      <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{tempo.desc}</p>
                    </button>
                  );
                })}
              </div>

              {/* Custom Date Picker */}
              <AnimatePresence>
                {selected === "custom" && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="pace-date-picker">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold text-slate-600">Pick your available days</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{customDates.length} selected</span>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                          <div key={i} className="text-center text-[8px] font-bold text-slate-400 py-0.5">{d}</div>
                        ))}
                        {(() => {
                          const dates = getDateRange();
                          const firstDay = dates[0].getDay();
                          const blanks = Array(firstDay).fill(null);
                          return [...blanks, ...dates].map((date, i) => {
                            if (!date) return <div key={`b-${i}`} />;
                            const dateStr = date.toISOString().split("T")[0];
                            const isChosen = customDates.includes(dateStr);
                            const dayNum = date.getDate();
                            return (
                              <button key={dateStr} onClick={() => toggleDate(dateStr)}
                                className={`py-1 rounded-lg text-[11px] font-medium transition-all ${isChosen ? "text-white shadow-sm" : "text-slate-600 hover:bg-slate-200"}`}
                                style={isChosen ? { backgroundColor: color } : {}}
                              >{dayNum}</button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Expiration Warnings */}
              {warnings.length > 0 && (
                <div className="space-y-1.5" data-testid="pace-warnings">
                  {warnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[11px] font-semibold text-amber-800">{w.stop_name}</p>
                        <p className="text-[10px] text-amber-600">{w.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm Button */}
              <button
                onClick={handleConfirm}
                disabled={loading || (!isChanged && selected !== "custom")}
                className="w-full py-3 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-40"
                style={{ backgroundColor: isChanged ? color : "#94a3b8" }}
                data-testid="confirm-pace-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />}
                {loading ? "Rescheduling..." : isChanged ? "Apply New Pace" : "No Change"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
